import $store from '@/store';
import $router from '@/router/index';

import { AuthService } from '@/services/api/auth.service';
import { notificationError } from '@/utils/notification';
import { usePermissions } from '@/hooks/usePermissions';
import {
  capabilityResourceGroupMap,
  resouceGroupRouteMap,
  whiteListedRoutes,
  whiteListedCapabilities,
  publicRoutes,
} from '@/router/constant';
import { useDispatch } from '@/utils/useMappers';
import { parseServiceObjFromApplication } from '@/utils/helper-function';
import { permissionTypes } from '@/router/constant';
import { setRedirectionServiceWithPermissions } from '@/router/utils';
import { useTime } from '@/hooks/useTime';
import { resourceGroupRouteList } from '@/router/constant';
import i18n from '@/plugins/i18n';

/**
 * Current user state initialization
 * @WARN Must be always first in middleware chain
 * @deprecated
 */
export async function initCurrentUserStateMiddleware(to, from, next) {
  if (AuthService.hasRefreshToken()) {
    try {
      await AuthService.debounceRefreshTokens();
      //   await $store.dispatch('user/getCurrent');
      next();
    } catch (e) {
      console.log(e);
    }
  } else {
    return next({ name: 'login' });
  }
}

export async function checkUserPermission(to, from, next) {
  const {
    getPermissions,
    hasCapabilityPermission,
    hasCapabilityAlertPermission,
  } = usePermissions();
  const [setTemplate] = useDispatch(['globalState/app/setTemplate']);

  const secondRoute = to.matched[1]?.name;
  if (publicRoutes.includes(secondRoute)) {
    return next();
  }

  try {
    $store.commit('globalState/app/setMiddlewareLoading', true);

    const permission = await getPermissions();

    if (
      permission?.[resouceGroupRouteMap[secondRoute]] ||
      permission?.[capabilityResourceGroupMap.CONFIG] ||
      whiteListedRoutes.includes(to.name) ||
      whiteListedCapabilities.includes(secondRoute)
    ) {
      next();
    } else {
      if (to.name === resouceGroupRouteMap.home?.toLowerCase()) {
        const firstAccessibleCapability = resourceGroupRouteList.find((item) =>
          hasCapabilityPermission(item.resourceGroup),
        );
        if (firstAccessibleCapability) {
          return next({ name: firstAccessibleCapability.name });
        } else {
          const onlyAlertAccessible = resourceGroupRouteList.find((item) =>
            hasCapabilityAlertPermission(item.resourceGroup),
          );
          return onlyAlertAccessible
            ? next({ name: 'generatedV2' }) // redirecting to alert page
            : next({ name: 'permissionDenied' });
        }
      }
      setTemplate();
      notificationError(i18n.global.t('permission-denied'));
      return next({ name: 'permissionDenied' });
    }
  } catch (error) {
    console.log(error);
  } finally {
    $store.commit('globalState/app/setMiddlewareLoading', false);
  }
}

async function fetchTenantName(teamId) {
  try {
    const response = await AuthService.getTeamName(teamId);
    return response?.data?.teams?.[0]?.name ?? '';
  } catch (error) {
    console.error('Error fetching tenant name:', error);
    return undefined;
  }
}

export async function checkServicePermission(to, from, next) {
  const serviceId = to?.query?.id ?? '';
  const category = to?.query.cat ?? '';
  const time = to?.query?.time;
  const retainRedirectionState = to?.query?.retainRedirectionState ?? false;
  const updatedQuery = { ...to.query };
  if (time) {
    const { setDurationFromRoute } = useTime();
    setDurationFromRoute(parseInt(time));
    delete updatedQuery.time;
  }
  if (serviceId) {
    try {
      $store.commit('globalState/app/setMiddlewareLoading', true);
      const service = parseServiceObjFromApplication({ key: serviceId });
      const currentTeamId = $store.state.globalState.user.teamID;
      if (currentTeamId !== service.teamID) {
        const teamNameData = await fetchTenantName(service.teamID);
        if (!teamNameData) {
          return;
        }
        $store.commit('globalState/user/setTeamId', service.teamID);
        $store.commit('globalState/user/setTeamName', teamNameData); // Use the updated value
        next({
          name: 'loadingPage',
          query: {
            ...to.query,
            updateAuth: 'true',
            retainRedirectionState: 'true',
            redirectPath: to.path,
          },
        });
      }
      const { fetchServicePermissions } = usePermissions();
      let serviceAccessList = [];
      if (to.name == 'generatedV2') {
        serviceAccessList = [
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.ALERT_READ,
          },
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.ALERT_WRITE,
          },
        ];
      } else {
        serviceAccessList = [
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.READ,
          },
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.WRITE,
          },
        ];
      }
      const permissionList = await fetchServicePermissions(serviceAccessList);
      if (permissionList?.length === 0) {
        notificationError(i18n.global.t('permission-denied'));
        next({ name: 'permissionDenied' });
      } else if (retainRedirectionState) {
        next();
      } else {
        setRedirectionServiceWithPermissions(
          to,
          serviceId,
          service,
          permissionList,
          category,
        );
        delete updatedQuery.id;
        delete updatedQuery.cat;
        next(next({ name: to.name, params: to.params, query: updatedQuery }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      $store.commit('globalState/app/setMiddlewareLoading', false);
    }
  } else {
    next();
  }
}

/**
 * Check access permission to auth routes
 */
export async function checkAccessMiddleware(to, from, next) {
  const isAuthRoute = to.matched.some((item) => item.meta.isAuth);

  if (isAuthRoute && $store.state.globalState.user.isAuthenticated)
    return next();
  if ($store.state.globalState.user.isAuthenticated && to.meta.disAllowOnAuth) {
    return $router.push('/home');
  }

  if (isAuthRoute && !$store.state.globalState.user.isAuthenticated) {
    // await $store.dispatch('globalState/user/getProfile');
    if (to && to.query) {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }
    return next({ name: 'login' });
  }
  next();
}

// export function setPageTitleMiddleware(to, from, next) {
//   const pageTitle = to.matched.find(item => item.meta.title);

//   if (pageTitle) window.document.title = pageTitle.meta.title;
//   next();
// }
